import type { PreisToolDispositionSettingsValues } from '@/api/preis-tool/preis-tool.types';

/**
 * Settings of the purchasing aid (EOS-62): form conversion, validation and
 * percent formatting for `DispositionSettingsDialog` and `DispositionBlock`.
 * Shares are stored as fractions (0.03) and typed in as percent (3).
 */

/**
 * Allowed ranges — mirror of `DISPOSITION_SETTINGS_LIMITS` in the backend
 * (`preis-tool/domain/disposition.ts`), which validates them again on save.
 */
export const DISPOSITION_SETTINGS_LIMITS = {
  salesPeriodDays: { min: 7, max: 365 },
  defaultLeadTimeDays: { min: 0, max: 180 },
  safetyDays: { min: 0, max: 180 },
  reorderHorizonDays: { min: 1, max: 365 },
  /** In percent. */
  outlierSharePct: { min: 5, max: 100 },
  /** In percent. */
  targetMarginPct: { min: 0.1, max: 50 },
  targetMarginCount: { min: 1, max: 6 },
} as const;

/** A share as percent: `0.025` → "2,5%", `0.0125` → "1,25%". */
export const fmtSharePct = (share: number, minDigits = 0) =>
  (share * 100).toLocaleString('de-DE', {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: 2,
  }) + '%';

/** Label of a target margin, e.g. "3,0%" or "1,25%". */
export const fmtTargetMargin = (share: number) => fmtSharePct(share, 1);

/** A share as typed percent: `0.025` → "2,5". */
const toPctInput = (share: number) =>
  (share * 100).toLocaleString('de-DE', {
    maximumFractionDigits: 2,
    useGrouping: false,
  });

/**
 * A typed percent ("2,5", "2.5", "2,5 %") as share, if it lies in `range`
 * (percent) and has at most two decimals; `null` otherwise.
 */
function parsePctInput(
  input: string,
  range: { min: number; max: number },
): number | null {
  const cleaned = input.trim().replace(/\s*%$/, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const pct = Number(cleaned);
  if (pct < range.min || pct > range.max) return null;
  // Rounded to four decimals of the share: 1,1 % is 0.011, not 0.011000000000000001.
  return Math.round(pct * 100) / 10000;
}

export type DispositionDayField =
  | 'salesPeriodDays'
  | 'defaultLeadTimeDays'
  | 'safetyDays'
  | 'reorderHorizonDays';

/** The day values in dialog order, with label and explanation. */
export const DISPOSITION_DAY_FIELDS: {
  key: DispositionDayField;
  label: string;
  hint: string;
}[] = [
  {
    key: 'salesPeriodDays',
    label: 'Absatz berechnen über',
    hint: 'Zeitraum für Absatz pro Tag, Reichweite und die Verkaufsliste',
  },
  {
    key: 'defaultLeadTimeDays',
    label: 'Lieferzeit, wenn das Angebot keine nennt',
    hint: 'gilt auch bei einer ungültigen Angabe des Feeds (z. B. −1)',
  },
  {
    key: 'safetyDays',
    label: 'Sicherheitszeit',
    hint: 'Puffer auf die Lieferzeit beim Meldebestand',
  },
  {
    key: 'reorderHorizonDays',
    label: 'Nachkauf für',
    hint: 'die vorgeschlagene Nachkaufmenge deckt den Absatz so vieler Tage',
  },
];

/** What the dialog edits: numbers as typed text, target margins as shares. */
export interface DispositionSettingsForm {
  salesPeriodDays: string;
  defaultLeadTimeDays: string;
  safetyDays: string;
  reorderHorizonDays: string;
  outlierSharePct: string;
  /** Ascending. */
  targetMargins: number[];
  /** `null` after the default was removed from the list. */
  defaultTargetMargin: number | null;
}

export function toDispositionSettingsForm(
  values: PreisToolDispositionSettingsValues,
): DispositionSettingsForm {
  return {
    salesPeriodDays: String(values.salesPeriodDays),
    defaultLeadTimeDays: String(values.defaultLeadTimeDays),
    safetyDays: String(values.safetyDays),
    reorderHorizonDays: String(values.reorderHorizonDays),
    outlierSharePct: toPctInput(values.outlierShare),
    targetMargins: [...values.targetMargins].sort((a, b) => a - b),
    defaultTargetMargin: values.defaultTargetMargin,
  };
}

/** The values to save, or the first problem in German. */
export function fromDispositionSettingsForm(
  form: DispositionSettingsForm,
): { values: PreisToolDispositionSettingsValues } | { error: string } {
  const limits = DISPOSITION_SETTINGS_LIMITS;
  const days: Partial<Record<DispositionDayField, number>> = {};
  for (const field of DISPOSITION_DAY_FIELDS) {
    const text = form[field.key].trim();
    const { min, max } = limits[field.key];
    const value = /^\d+$/.test(text) ? Number(text) : NaN;
    if (!(value >= min && value <= max)) {
      return {
        error: `${field.label}: bitte eine ganze Zahl von ${min} bis ${max} Tagen.`,
      };
    }
    days[field.key] = value;
  }

  const outlierShare = parsePctInput(form.outlierSharePct, limits.outlierSharePct);
  if (outlierShare == null) {
    const { min, max } = limits.outlierSharePct;
    return {
      error: `Warnung bei einem großen Auftrag: bitte einen Prozentwert von ${min} bis ${max} mit höchstens zwei Nachkommastellen.`,
    };
  }

  const count = form.targetMargins.length;
  if (count < limits.targetMarginCount.min || count > limits.targetMarginCount.max) {
    return {
      error: `Bitte ${limits.targetMarginCount.min} bis ${limits.targetMarginCount.max} Zielmargen angeben.`,
    };
  }
  if (form.defaultTargetMargin == null || !form.targetMargins.includes(form.defaultTargetMargin)) {
    return { error: 'Bitte eine der Zielmargen als Standard wählen.' };
  }

  return {
    values: {
      salesPeriodDays: days.salesPeriodDays!,
      defaultLeadTimeDays: days.defaultLeadTimeDays!,
      safetyDays: days.safetyDays!,
      reorderHorizonDays: days.reorderHorizonDays!,
      outlierShare,
      targetMargins: form.targetMargins,
      defaultTargetMargin: form.defaultTargetMargin,
    },
  };
}

/** A typed target margin to add to the list, or why it cannot be added. */
export function parseNewTargetMargin(
  input: string,
  existing: number[],
): { share: number } | { error: string } {
  const share = parsePctInput(input, DISPOSITION_SETTINGS_LIMITS.targetMarginPct);
  if (share == null) {
    const { min, max } = DISPOSITION_SETTINGS_LIMITS.targetMarginPct;
    return {
      error: `Zielmarge: bitte einen Prozentwert von ${min.toLocaleString('de-DE')} bis ${max} mit höchstens zwei Nachkommastellen.`,
    };
  }
  if (existing.includes(share)) {
    return { error: `Die Zielmarge ${fmtTargetMargin(share)} steht schon in der Liste.` };
  }
  if (existing.length >= DISPOSITION_SETTINGS_LIMITS.targetMarginCount.max) {
    return {
      error: `Höchstens ${DISPOSITION_SETTINGS_LIMITS.targetMarginCount.max} Zielmargen.`,
    };
  }
  return { share };
}
